import * as React from 'react'
import { useEffect, useRef, useState } from 'react'

import { useTransactionsList } from './hooks'
import { Client, Gateway, Transaction } from './types'
import { CButton, CCardBody, CCol, CRow } from '@coreui/react-pro'
import {
  Button,
  DatePicker,
  Divider,
  Input,
  InputRef,
  Select,
  Space,
  Table,
  Tag,
} from 'antd'
import { path, role } from '../../../../libs'
import { transactionRequestDirection } from '../../const'
import { ColumnsType } from 'antd/lib/table'
import CIcon from '@coreui/icons-react'
import { cilZoom } from '@coreui/icons'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import { FilterValue } from 'antd/es/table/interface'
import { cilArrowLeft } from '@coreui/icons/js/free/cil-arrow-left'
import { useStore } from 'effector-react'
import { $userRole } from '../../../../features/user'
import dayjs from 'dayjs'
import { rangePresets } from '../../../billing/transactions/list/consts'
import isBetween from 'dayjs/plugin/isBetween'

type DataIndex = keyof Transaction

export const TransactionList = () => {
  const {
    transactions,
    totalCountOfTransactions,
    loading,
    onChangeTransactionsList,
  } = useTransactionsList()

  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({
    // @ts-ignore
    updatedAt: [
      dayjs(new URLSearchParams(window.location.search).get('updatedAt')),
      dayjs(new URLSearchParams(window.location.search).get('updatedAt')),
    ],
  })
  const [orderBy, setOrderBy] = useState('updatedAt')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({
    status: ['TRANSACTION_REQUEST_STATUS_ACCEPTED'],
  })

  const userRole = useStore($userRole)

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  useEffect(() => {
    onChangeTransactionsList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      searchText: searchText,
      filters: filtersState,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText, filtersState])

  const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
    clearFilters()
    setSearchText((prev) => {
      delete prev[dataIndex]
      return {
        ...prev,
      }
    })
  }

  const getColumnSearchProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            setSearchText((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }))
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              setSearchText((prev) => ({
                ...prev,
                [dataIndex]: selectedKeys[0],
              }))
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchText[dataIndex] ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText[dataIndex]]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const csvContent = transactions
    .map((item) => Object.values(item).join(','))
    .join('\n')

  const csvCode =
    'data:text/csv;charset=utf-8,SEP=,%0A' + encodeURIComponent(csvContent)

  const columns: ColumnsType<Transaction> = [
    {
      title: 'ID',
      dataIndex: 'transactionId',
      ...getColumnSearchProps('transactionId'),
      sorter: true,
    },
    {
      title: 'Client',
      children: [
        {
          title: 'ID',
          dataIndex: 'client',
          key: 'clientId',
          render: (item: Client) => (
            <>
              <a href={path.clients.details(item.clientId)} target="_blank">
                {item.clientId}
              </a>
            </>
          ),
        },
        {
          title: 'Full name',
          dataIndex: 'client',
          key: 'clientId',
          render: (item: Client) => (
            <>
              <div>{item.fullName}</div>
            </>
          ),
        },
        {
          title: 'Country',
          dataIndex: 'client',
          key: 'clientId',
          render: (item: Client) => (
            <>
              <div>{item.country}</div>
            </>
          ),
        },
      ],
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      render: (item, record) => <div>{record.humanCreatedAt}</div>,
      sorter: true,
    },
    {
      title: 'Updated at',
      dataIndex: 'updatedAt',
      render: (item, record) => <div>{record.humanUpdatedAt}</div>,
      defaultSortOrder: 'descend',
      sorter: true,
    },
    {
      title: 'Method',
      dataIndex: 'gateway',
      render: (item: Gateway) => <>{item?.internalName}</>,
    },
    {
      title: 'Initial amount USD',
      dataIndex: 'initialAmountUsd',
      sorter: true,
      render: (item, record, index) => {
        if (record.direction == 'TRANSACTION_REQUEST_DIRECTION_IN') {
          return (
            <>
              <Tag color="success">
                <strong>{parseFloat(item).toFixed(2)}</strong>{' '}
              </Tag>
              USD
            </>
          )
        } else {
          return (
            <>
              <Tag color="error">
                <strong>{parseFloat(item).toFixed(2)}</strong>{' '}
              </Tag>
              USD
            </>
          )
        }
      },
    },
    {
      title: 'Initial amount',
      dataIndex: 'initialAmount',
      sorter: true,
      render: (item, record, index) => {
        if (record.direction == 'TRANSACTION_REQUEST_DIRECTION_IN') {
          return (
            <>
              <Tag color="success">
                <strong>{parseFloat(item).toFixed(2)}</strong>{' '}
              </Tag>
              {record.initialCurrency}
            </>
          )
        } else {
          return (
            <>
              <Tag color="error">
                <strong>{parseFloat(item).toFixed(2)}</strong>{' '}
              </Tag>
              {record.initialCurrency}
            </>
          )
        }
      },
    },
    {
      title: 'Client amount',
      dataIndex: 'clientAmount',
      sorter: true,
      render: (item, record, index) => {
        if (record.direction == 'TRANSACTION_REQUEST_DIRECTION_IN') {
          return (
            <>
              <Tag color="success">
                <strong>{parseFloat(item).toFixed(2)}</strong>{' '}
              </Tag>
              {record.clientCurrency}
            </>
          )
        } else {
          return (
            <>
              <Tag color="error">
                <strong>{parseFloat(item).toFixed(2)}</strong>{' '}
              </Tag>
              {record.clientCurrency}
            </>
          )
        }
      },
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 100,
      render: (item) => {
        return (
          <>
            <a
              className="btn btn-secondary me-2"
              title="View"
              target="_blank"
              href={
                item.direction == transactionRequestDirection.In()
                  ? path.billing.deposit.edit(item.transactionId)
                  : path.billing.withdrawal.edit(item.transactionId)
              }
            >
              <CIcon icon={cilZoom} />
            </a>
          </>
        )
      },
    },
  ]
  const handleChangeTypeSelect = (value: FilterValue) => {
    if (value) {
      setFiltersState((prevState) => ({
        ...prevState,
        direction: value,
      }))
    } else {
      setFiltersState((prevState) => {
        delete prevState['direction']
        return {
          ...prevState,
        }
      })
    }
  }

  const isDisplayDownloadButton =
    userRole?.filter((roleItem) =>
      [role.root(), role.head.cfo(), role.finances.financeManager()].includes(
        roleItem,
      ),
    ).length !== 0
  return (
    <CCardBody>
      <CRow>
        <CCol xs={7}>
          {isDisplayDownloadButton && (
            <CButton
              color="primary"
              href={csvCode}
              download="coreui-table-data.csv"
              target="_blank"
            >
              Download current items (.csv)
            </CButton>
          )}
        </CCol>
        <CCol
          xs={4}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '5px',
          }}
        >
          <div>Type</div>
          <Select
            // @ts-ignore
            defaultValue=""
            style={{ width: 250 }}
            onChange={(value: FilterValue) => handleChangeTypeSelect(value)}
            options={[
              { value: '', label: 'All' },
              { value: transactionRequestDirection.In(), label: 'Deposits' },
              {
                value: transactionRequestDirection.Out(),
                label: 'Withdrawal',
              },
            ]}
          />
        </CCol>
        <CCol
          xs={1}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <CButton
            color="secondary"
            className="me-2"
            title={'<- Back'}
            href={
              path.kpi.details() +
              '?month=' +
              new URLSearchParams(window.location.search).get('updatedAt')
            }
          >
            <CIcon icon={cilArrowLeft} /> {'Back'}
          </CButton>
        </CCol>
      </CRow>
      <Divider />
      <Table
        dataSource={transactions}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        onRow={(record, rowIndex) => {
          return {
            onClick: (event) => {},
          }
        }}
        rowKey="transactionId"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfTransactions,
        }}
        onChange={(pagination, filters, sorter, extra) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
          })
        }
      />
    </CCardBody>
  )
}
